import { type ReactNode, type RefObject } from 'react';

import { useRichInitialDetailsStateClosedClassRef } from '#/snippets/rich-details-snippet';
import {
  richDetailsAttr,
  richDetailsDesktopTriggersOnHoverAttr,
  richDetailsSingletonAttr,
} from '#/snippets/snippet-attributes';
import {
  getTrackClickedAttributes,
  type TrackClickedData,
} from '#/snippets/track-clicked-snippet';

import { DropdownDetails, DropdownSummary } from './Dropdown.styled';

type Props = {
  detailsRef?: RefObject<HTMLDetailsElement | null>;
  /**
   * Dropdown uses `<summary>` + `<details>` so the trigger should not be a `<button>`
   * or other element that hijacks the `onClick` event
   */
  trigger: ReactNode;
  children: ReactNode;
  qaId?: string;
  desktopTriggersOnHover?: boolean;
  summaryAriaLabel?: string;
  trackClickedOpen?: TrackClickedData;
};

export function Dropdown({
  detailsRef,
  trigger,
  children,
  qaId,
  desktopTriggersOnHover,
  summaryAriaLabel,
  trackClickedOpen,
}: Props) {
  return (
    <DropdownDetails
      ref={detailsRef}
      className={useRichInitialDetailsStateClosedClassRef().current}
      data-qa-id={qaId}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...{
        [richDetailsSingletonAttr]: true,
        [richDetailsAttr]: true,
        [richDetailsDesktopTriggersOnHoverAttr]: desktopTriggersOnHover
          ? true
          : undefined,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(trackClickedOpen
        ? getTrackClickedAttributes(trackClickedOpen)
        : undefined)}
      suppressHydrationWarning
    >
      <DropdownSummary aria-label={summaryAriaLabel}>{trigger}</DropdownSummary>

      {children}
    </DropdownDetails>
  );
}
